import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Mail, RotateCw, Loader2, Ban, Search, X, Building2, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { customersAPI } from '../../lib/api';
import {
  emailQueueAPI,
  type EmailQueueEntry,
  type EmailQueueTypeFacet,
  type EmailQueueStats,
} from '../../lib/api/emailQueue';
import { QueueRow, statusLabel, effectiveStatus } from './EmailQueueRow';
import { useView } from '../../contexts/ViewContext';

interface CustomerResult { company: number; company_name: string }

// Status tabs (value, label, and which stat drives the count badge).
const STATUS_TABS: { value: string; label: string; statKey?: keyof EmailQueueStats }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending', statKey: 'pending' },
  { value: 'sending', label: 'Sending', statKey: 'sending' },
  { value: 'sent', label: 'Sent', statKey: 'sent_24h' },
  { value: 'failed', label: 'Failed', statKey: 'failed' },
  { value: 'skipped', label: 'Skipped', statKey: 'skipped' },
  { value: 'rate_limited', label: 'Rate Limited', statKey: 'rate_limited' },
  { value: 'cancelled', label: 'Cancelled', statKey: 'cancelled' },
];

function PaginationBar({ page, totalPages, totalCount, onPrev, onNext }: {
  page: number; totalPages: number; totalCount: number;
  onPrev: () => void; onNext: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">
        Page {page} of {totalPages} ({totalCount} total)
      </p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={onPrev}>
          Previous
        </Button>
        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
}

/**
 * Turn a notification_type key into a readable label.
 *
 * Derived rather than looked up in a map so a type the backend starts
 * sending tomorrow reads correctly today, which is the point of driving the
 * filter from data. Matches how the table renders the same value.
 *
 * @param value - snake_case type key, e.g. "order_progress_digest"
 * @returns Title-cased label, e.g. "Order Progress Digest"
 */
const TYPE_ACRONYMS = new Set(['po', 'grn', 'ncr', 'asn', 'otd', 'otif', 'cofc', 'erp']);

function humaniseType(value: string): string {
  return value
    .split('_')
    .map(word => (
      TYPE_ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1)
    ))
    .join(' ');
}

export default function EmailQueueDashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState<EmailQueueStats | null>(null);
  const [entries, setEntries] = useState<EmailQueueEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  // The Type options come from the data. A hardcoded list drifted in both
  // directions — offering types this deployment never sends, and missing one
  // it does — so the filter advertised empty results and hid real ones.
  const [typeOptions, setTypeOptions] = useState<EmailQueueTypeFacet[]>([]);
  const [exporting, setExporting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Customer (company) filter
  const [company, setCompany] = useState<CustomerResult | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState<CustomerResult[]>([]);

  // Action loading states
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Email-body viewer
  const [viewEntry, setViewEntry] = useState<EmailQueueEntry | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const openBody = async (id: number) => {
    setViewLoading(true);
    try {
      const res = await emailQueueAPI.getEntry(id);
      setViewEntry(res.data);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to load email body');
    } finally {
      setViewLoading(false);
    }
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 400);
  }, []);

  // Customer search typeahead
  useEffect(() => {
    const q = customerSearch.trim();
    if (q.length < 2) { setCustomerResults([]); return; }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const res = await customersAPI.getProfiles({ search: q, page_size: 8 });
        if (live) setCustomerResults((res.data.results || []) as unknown as CustomerResult[]);
      } catch { if (live) setCustomerResults([]); }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [customerSearch]);

  // Scope the monitor to the site selected in the header switcher, so it
  // shows the outgoing mail for the site the admin is looking at.
  // selectedCompanyId is null for a site-restricted admin, hence the fall back
  // to selectedSiteId — the same pairing the dashboard scopes on.
  const { selectedCompanyId, selectedSiteId, selectedCompanyName, selectedSiteName } = useView();
  const activeSiteId = selectedCompanyId ?? selectedSiteId;
  const activeSiteName = selectedCompanyName ?? selectedSiteName;

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const params: Record<string, string | number | boolean> = {
        page,
        page_size: pageSize,
        ordering: '-created_at',
      };
      if (statusFilter !== 'all') params['status'] = statusFilter;
      if (typeFilter !== 'all') params['notification_type'] = typeFilter;
      if (search) params['search'] = search;
      if (company) params['company'] = company.company;
      if (activeSiteId) params['site'] = activeSiteId;

      // Stats and the type facets share the list's scope so the tab counts
      // and filter options can never contradict the rows.
      const scope: Record<string, string | number> = {};
      if (activeSiteId) scope['site'] = activeSiteId;
      if (company) scope['company'] = company.company;

      const [statsRes, entriesRes, typesRes] = await Promise.all([
        emailQueueAPI.getStats(activeSiteId ? { site: activeSiteId } : undefined),
        emailQueueAPI.getEntries(params),
        emailQueueAPI.getNotificationTypes(scope),
      ]);

      setStats(statsRes.data);
      setEntries(entriesRes.data.results);
      setTotalCount(entriesRes.data.count);
      setTypeOptions(typesRes.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load email queue data';
      toast.error(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, statusFilter, typeFilter, search, company, activeSiteId]);

  /**
   * Download the current view as an Excel workbook.
   *
   * Sends the same filters the list is showing rather than the page of rows
   * on screen: the export is of what you filtered to, not what happens to be
   * paginated in front of you.
   */
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params: Record<string, string | number | boolean> = { ordering: '-created_at' };
      if (statusFilter !== 'all') params['status'] = statusFilter;
      if (typeFilter !== 'all') params['notification_type'] = typeFilter;
      if (search) params['search'] = search;
      if (company) params['company'] = company.company;
      if (activeSiteId) params['site'] = activeSiteId;

      const res = await emailQueueAPI.exportEntries(params);
      const url = window.URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `email-queue-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Export failed';
      toast.error(message);
    } finally {
      setExporting(false);
    }
  }, [statusFilter, typeFilter, search, company, activeSiteId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleCancel = async (id: number) => {
    setCancellingId(id);
    try {
      await emailQueueAPI.cancelEntry(id);
      toast.success('Email cancelled');
      fetchData(true);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel email');
    } finally {
      setCancellingId(null);
    }
  };

  const handleRetry = async (id: number) => {
    setRetryingId(id);
    try {
      await emailQueueAPI.retryEntry(id);
      toast.success('Email queued for retry');
      fetchData(true);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to retry email');
    } finally {
      setRetryingId(null);
    }
  };

  const handleCancelAll = async () => {
    setBulkActionLoading(true);
    try {
      await emailQueueAPI.cancelAll();
      toast.success('All pending emails cancelled');
      fetchData(true);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel all emails');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleRetryAll = async () => {
    setBulkActionLoading(true);
    try {
      await emailQueueAPI.retryAll();
      toast.success('All failed emails queued for retry');
      fetchData(true);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to retry all emails');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const selectCompany = (c: CustomerResult) => {
    setCompany(c);
    setCustomerSearch('');
    setCustomerResults([]);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Mail className="h-6 w-6" />
            Email Queue
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {activeSiteName ? (
              <>
                Outbound email for <strong>{activeSiteName}</strong> — switch site
                in the header to see another. History is kept for the last 30 days.
              </>
            ) : (
              <>Monitor and manage outbound email delivery. History is kept for the last 30 days.</>
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchData(true)} disabled={refreshing}>
          <RotateCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Status tabs (counts double as the at-a-glance summary) */}
      <Tabs value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
        <TabsList className="flex flex-wrap h-auto">
          {STATUS_TABS.map(t => {
            const count = t.statKey && stats ? (stats[t.statKey] ?? 0) : null;
            return (
              <TabsTrigger key={t.value} value={t.value} className="gap-1.5">
                {t.label}
                {count !== null && (
                  <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums">{count}</span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Filters + bulk actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <Input
          placeholder="Search recipient..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="w-full sm:w-56"
        />

        {/* Customer filter */}
        {company ? (
          <div className="flex items-center gap-1.5 rounded-md border bg-muted/40 px-2.5 py-1.5 text-sm">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="max-w-[160px] truncate">{company.company_name}</span>
            <button type="button" onClick={() => { setCompany(null); setPage(1); }} aria-label="Clear customer filter">
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        ) : (
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Filter by customer…"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="pl-8"
            />
            {customerResults.length > 0 && (
              <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md">
                {customerResults.map((c) => (
                  <button
                    key={c.company}
                    type="button"
                    onClick={() => selectCompany(c)}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    {c.company_name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {typeOptions.map(({ value, count }) => (
              <SelectItem key={value} value={value}>
                {humaniseType(value)} ({count})
              </SelectItem>
            ))}
            {/* A type can be selected and then filtered out of scope (site
                switch, customer filter). Keep it listed so the dropdown does
                not show a blank trigger for the filter that is actually on. */}
            {typeFilter !== 'all' && !typeOptions.some(t => t.value === typeFilter) && (
              <SelectItem value={typeFilter}>{humaniseType(typeFilter)} (0)</SelectItem>
            )}
          </SelectContent>
        </Select>

        <div className="flex-1" />

        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={exporting || totalCount === 0}
          title="Download the filtered rows as an Excel workbook"
        >
          {exporting
            ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            : <Download className="h-4 w-4 mr-1" />}
          Export
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" disabled={bulkActionLoading || (stats?.pending === 0)}>
              <Ban className="h-4 w-4 mr-1" />
              Cancel All Pending
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel all pending emails?</AlertDialogTitle>
              <AlertDialogDescription>
                This will cancel {stats?.pending ?? 0} pending email(s). This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Go Back</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancelAll} className="bg-red-600 hover:bg-red-700">
                Cancel All
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button
          variant="outline"
          size="sm"
          className="text-amber-600 border-amber-200 hover:bg-amber-50"
          disabled={bulkActionLoading || (stats?.failed === 0)}
          onClick={handleRetryAll}
        >
          <RotateCw className="h-4 w-4 mr-1" />
          Retry All Failed
        </Button>
      </div>

      {/* Top pagination */}
      {totalCount > pageSize && (
        <PaginationBar
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          onPrev={() => setPage(p => p - 1)}
          onNext={() => setPage(p => p + 1)}
        />
      )}

      {/* Queue Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Sent / Failed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                    <Mail className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    No emails found
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <QueueRow
                    key={entry.id}
                    entry={entry}
                    cancellingId={cancellingId}
                    retryingId={retryingId}
                    onCancel={handleCancel}
                    onRetry={handleRetry}
                    onView={openBody}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Bottom pagination */}
      {totalCount > pageSize && (
        <PaginationBar
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          onPrev={() => setPage(p => p - 1)}
          onNext={() => setPage(p => p + 1)}
        />
      )}

      {/* Email body viewer */}
      <Dialog open={Boolean(viewEntry) || viewLoading} onOpenChange={(o) => { if (!o) setViewEntry(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-base">{viewEntry?.subject || 'Email'}</DialogTitle>
            <DialogDescription>
              To {viewEntry?.recipient_email}
              {viewEntry?.recipient_company ? ` · ${viewEntry.recipient_company}` : ''}
              {viewEntry ? ` · ${statusLabel(effectiveStatus(viewEntry))}` : ''}
            </DialogDescription>
          </DialogHeader>
          {viewLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : viewEntry?.body_html ? (
            <iframe
              title="Email body"
              sandbox=""
              className="w-full flex-1 min-h-[50vh] rounded border bg-white"
              srcDoc={viewEntry.body_html}
            />
          ) : (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {viewEntry ? 'No HTML body stored for this email.' : ''}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
